import { faEdit } from "@fortawesome/pro-regular-svg-icons";
import {
  faCircle,
  faUpRightFromSquare,
} from "@fortawesome/pro-solid-svg-icons";

import { Button } from "src/base-components/Button";
import { Card } from "src/base-components/Card";
import { ExternalLink } from "src/base-components/ExternalLink";
import { Icon } from "src/base-components/Icon";
import {
  Agent,
  AgentVersion,
  AgentVersionStatus,
} from "src/clients/agent-management";
import { Tooltip } from "src/design-system/Tooltip";
import { UserNameInfo } from "src/flowContainer/versionEditedInfo/UsernameInfo";
import { FEATURE_FLAGS } from "src/router/featureFlags";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { getBaseUrl, getUrlToAgentVersionPage } from "src/router/urls";
import { formatDate } from "src/utils/datetime";

type Props = {
  agent: Agent;
  version: AgentVersion;
  immutable: boolean;
  handleChangeAgent: () => void;
};

const LastEdited: React.FC<{ version: AgentVersion }> = ({ version }) => {
  const userId =
    version.metadata.updated_by_id || version.metadata.created_by_id;

  return (
    <div className="flex flex-row items-center gap-x-1">
      <p className="font-inter-normal-12px text-gray-600">Edited by</p>
      <div className="flex items-center gap-x-1 font-inter-medium-12px text-gray-800">
        <UserNameInfo userId={userId} withAvatar />
      </div>
      <p className="font-inter-normal-12px text-gray-600">on</p>
      <p className="font-inter-medium-12px text-gray-800">
        {formatDate(version.metadata.updated_at)}
      </p>
    </div>
  );
};

const StatusIndicator: React.FC<{ status: AgentVersionStatus }> = ({
  status,
}) => {
  const isPublished = status === AgentVersionStatus.PUBLISHED;
  return (
    <Icon
      color={isPublished ? "text-green-400" : "text-yellow-400"}
      icon={faCircle}
      size="2xs"
    />
  );
};

export const SelectedAgentDisplay: React.FC<Props> = ({
  agent,
  version,
  handleChangeAgent,
  immutable,
}) => {
  const { orgId, workspace } = useAuthoringContext();
  const agentVersionUrl = `${getBaseUrl()}${getUrlToAgentVersionPage(
    orgId,
    workspace.id,
    agent.id,
    version.id,
    // Ensure agents flag is always included when opening in new tab
    { [FEATURE_FLAGS.agents]: "true" },
  )}`;

  return (
    <Card>
      <Card.Header>
        <div className="flex flex-row items-center gap-x-1">
          <StatusIndicator status={version.status} />
          <p
            className="font-inter-semibold-14px text-gray-800"
            data-loc="agent-selected-version-display-header"
          >
            {agent.name}, {version.name}
          </p>
        </div>

        <Tooltip placement="bottom-end" title="Open in new tab" asChild>
          <div>
            <ExternalLink className="text-gray-500" href={agentVersionUrl}>
              <Icon icon={faUpRightFromSquare} size="xs" />
            </ExternalLink>
          </div>
        </Tooltip>
      </Card.Header>
      <div className="space-y-1 pt-1">
        <p className="whitespace-pre-line font-inter-normal-12px text-gray-500">
          {agent.description}
        </p>
        <LastEdited version={version} />
      </div>
      <div className="mt-3">
        <Button
          disabled={immutable}
          iconLeft={faEdit}
          size="sm"
          variant="secondary"
          onClick={handleChangeAgent}
        >
          Change Agent
        </Button>
      </div>
    </Card>
  );
};
